import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore } from '@/store/dataset-store';
import { useUiStore } from '@/store/ui-store';
import { useWidgetsStore } from '@/store/widgets-store';
import { usePinnedFiltersStore } from '@/store/pinned-filters-store';
import { useImportConfigStore } from '@/store/import-config-store';
import { useModeStore } from '@/store/mode-store';
import { configDb } from '@/persistence/config-db';
import { ImportReviewModal } from '@/features/save-load/ImportReviewModal';

/**
 * Sidebar button under "Veri ayarları": opens the column-matching
 * review modal using the last imported config. If no config has been
 * imported yet in this session, the button is disabled with a tooltip.
 *
 * After applying, widgets / pinned filters / value groupings are
 * reloaded from Dexie.
 */
export function ColumnMatchCheckButton(): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const setValueGroupings = useDatasetStore((s) => s.setValueGroupings);
  const loadWidgets = useWidgetsStore((s) => s.loadForDataset);
  const loadPinned = usePinnedFiltersStore((s) => s.loadForDataset);
  const lastConfig = useImportConfigStore((s) => s.lastConfig);
  const mode = useModeStore((s) => s.mode);

  const open = useUiStore((s) => s.activeModal === 'column-match');
  const setActiveModal = useUiStore((s) => s.setActiveModal);

  const entry = useMemo(
    () => (activeId ? datasets.get(activeId) : undefined),
    [activeId, datasets],
  );

  if (!entry || !activeId) return null;

  const hasConfig = !!lastConfig;

  async function handleImported(): Promise<void> {
    if (!entry || !activeId) return;
    await loadWidgets(activeId, entry.columns, mode);
    await loadPinned(activeId, mode, {});
    try {
      const vg = await configDb.valueGroupings.get(activeId);
      setValueGroupings(activeId, vg?.groupings ?? {});
    } catch {
      setValueGroupings(activeId, {});
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setActiveModal('column-match')}
        disabled={!hasConfig}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left flex items-center justify-between gap-2 disabled:opacity-40"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
        title={
          hasConfig
            ? t('sidebar.button.columnMatchCheckEnabled')
            : t('sidebar.button.columnMatchCheckDisabled')
        }
      >
        <span>{t('sidebar.button.columnMatchCheck')}</span>
      </button>

      {open && lastConfig && (
        <ImportReviewModal
          open
          onClose={() => setActiveModal(null)}
          config={lastConfig}
          targetDatasetId={activeId}
          currentColumns={entry.columns}
          onImported={() => void handleImported()}
        />
      )}
    </>
  );
}
